/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * External dependencies
 */
import { useCallback, useEffect, useRef } from '@web-stories-wp/react';

/**
 * Internal dependencies
 */
import useRichTextFormatting from '../../textStyle/useRichTextFormatting';
import { useStory } from '../../../../../app/story';
import { useCanvas } from '../../../../../app';
import { usePresubmitHandler } from '../../../../form';
import getUpdatedSizeAndPosition from '../../../../../utils/getUpdatedSizeAndPosition';

function useApplyStyle({ pushUpdate }) {
  // Update size and position if relevant values have changed.
  usePresubmitHandler(getUpdatedSizeAndPosition, []);

  const { selectedElements } = useStory(({ state: { selectedElements } }) => {
    return {
      selectedElements,
    };
  });

  const extraPropsToAdd = useRef(null);
  const pendingBoxPreset = useRef(null);
  const push = useCallback(
    (updater) => {
      const extraProps = extraPropsToAdd.current;
      pushUpdate((oldProps) => {
        return {
          ...updater(oldProps),
          ...extraProps,
          ...('fontReferences' in oldProps
            ? { fontReferences: oldProps.fontReferences }
            : {}),
        };
      }, true);
      extraPropsToAdd.current = null;
    },
    [pushUpdate]
  );

  const {
    handlers: {
      handleSetColor,
      handleSetLetterSpacing,
      handleClickUnderline,
      handleClickItalic,
      handleSelectFontWeight,
      handleApplyTextStylePreset,
    },
    isEditingMixedTypography,
    hasActiveRange,
  } = useRichTextFormatting(selectedElements, push);

  const { clearEditing } = useCanvas(({ actions: { clearEditing } }) => ({
    clearEditing,
  }));

  const applyPresetToBox = useCallback(
    (preset) => {
      const {
        color,
        fontWeight,
        isItalic,
        isUnderline,
        letterSpacing,
        ...rest
      } = preset;
      extraPropsToAdd.current = rest;
      handleSetColor(color);
      handleSetLetterSpacing(letterSpacing);
      handleSelectFontWeight(fontWeight);
      handleClickUnderline(isUnderline);
      handleClickItalic(isItalic);
    },
    [
      handleSetColor,
      handleSetLetterSpacing,
      handleSelectFontWeight,
      handleClickUnderline,
      handleClickItalic,
    ]
  );

  useEffect(() => {
    if (isEditingMixedTypography || !pendingBoxPreset.current) {
      return;
    }

    const preset = pendingBoxPreset.current;
    pendingBoxPreset.current = null;
    applyPresetToBox(preset);
  }, [applyPresetToBox, isEditingMixedTypography]);

  const handleApplyStyle = useCallback(
    (preset) => {
      if (isEditingMixedTypography && hasActiveRange) {
        handleApplyTextStylePreset(preset);
        return;
      }

      if (isEditingMixedTypography) {
        pendingBoxPreset.current = preset;
        Promise.resolve(clearEditing()).catch(() => {
          pendingBoxPreset.current = null;
        });
        return;
      }

      applyPresetToBox(preset);
    },
    [
      applyPresetToBox,
      clearEditing,
      handleApplyTextStylePreset,
      hasActiveRange,
      isEditingMixedTypography,
    ]
  );
  return handleApplyStyle;
}

export default useApplyStyle;
